import dayjs, { Dayjs } from 'dayjs/esm';
import type { AncDatePickerValue } from '../anc-date-picker/anc-date-picker.models';

export type AncCustomDateRangePreset = 'today' | 'this_week' | 'this_month';

function toDayjs(value: unknown): Dayjs | null {
    if (!value) {
        return null;
    }
    const d = dayjs(value as never);
    return d.isValid() ? d : null;
}

/** Last 30 days ending today — shown on the trigger before anything is applied. */
export function defaultAncCustomDateRange(): AncDatePickerValue {
    const end = dayjs();
    const start = end.subtract(29, 'day');
    return { startDate: start, endDate: end };
}

/** Monday of the current week — `dayjs().day()` is 0 (Sun) – 6 (Sat), no plugin required. */
function startOfIsoWeek(date: Dayjs): Dayjs {
    const dayOfWeek = date.day();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return date.subtract(daysSinceMonday, 'day').startOf('day');
}

/** Calendar-to-date ranges: This Week = Monday through today, This Month = the 1st through today. */
export function computeAncCustomDateRangePreset(
    preset: AncCustomDateRangePreset
): AncDatePickerValue {
    const today = dayjs();
    switch (preset) {
        case 'today':
            return { startDate: today, endDate: today };
        case 'this_week':
            return { startDate: startOfIsoWeek(today), endDate: today };
        case 'this_month':
            return { startDate: today.startOf('month'), endDate: today };
    }
}

export const ANC_CUSTOM_DATE_RANGE_PRESETS: ReadonlyArray<{
    id: AncCustomDateRangePreset;
    label: string;
}> = [
    { id: 'today', label: 'Today' },
    { id: 'this_week', label: 'This Week' },
    { id: 'this_month', label: 'This Month' }
];

/** Which preset an applied range corresponds to, or null for a hand-picked range. */
export function matchAncCustomDateRangePreset(
    value: AncDatePickerValue | null
): AncCustomDateRangePreset | null {
    const start = toDayjs(value?.startDate);
    const end = toDayjs(value?.endDate);
    if (!start || !end) {
        return null;
    }
    const match = ANC_CUSTOM_DATE_RANGE_PRESETS.find(({ id }) => {
        const range = computeAncCustomDateRangePreset(id);
        return (
            start.isSame(toDayjs(range.startDate), 'day') &&
            end.isSame(toDayjs(range.endDate), 'day')
        );
    });
    return match?.id ?? null;
}

/** `START - END` display text, e.g. `19/11/2026 - 20/12/2026`. Empty string when value is unset. */
export function formatAncCustomDateRange(
    value: AncDatePickerValue | null,
    format: string
): string {
    const start = toDayjs(value?.startDate);
    const end = toDayjs(value?.endDate);
    if (!start || !end) {
        return '';
    }
    return `${start.format(format)} - ${end.format(format)}`;
}
